<template>
  <div class="app-container">
    <div class="toolbar">
      <el-select
        v-model="filters.status"
        clearable
        placeholder="Status"
        size="small"
        @change="handleFilterChange"
      >
        <el-option v-for="status in statuses" :key="status" :label="status" :value="status" />
      </el-select>
      <el-button size="small" type="primary" @click="loadQuotes">Refresh</el-button>
    </div>

    <el-table :data="quotes" border>
      <el-table-column prop="quoteNo" label="Quote" width="150" />
      <el-table-column prop="styleCode" label="Style Code" width="140" />
      <el-table-column prop="customerSizeSystem" label="Size System" width="120" />
      <el-table-column prop="customerSize" label="Size" width="90" />
      <el-table-column prop="sourceAvailability" label="Availability" width="190" />
      <el-table-column label="GetNice PHP Price" width="160">
        <template slot-scope="{ row }">
          {{ displayPrice(row) }}
        </template>
      </el-table-column>
      <el-table-column prop="status" label="Status" width="170" />
      <el-table-column prop="expiresAt" label="Expires" width="170" />
      <el-table-column label="Action" width="260">
        <template slot-scope="{ row }">
          <el-button size="mini" @click="openDetail(row)">Detail</el-button>
          <el-button
            size="mini"
            type="primary"
            :disabled="!isSendable(row)"
            @click="sendQuote(row)"
          >
            Send
          </el-button>
          <el-button size="mini" type="danger" @click="cancelQuote(row)">Cancel</el-button>
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination">
      <el-pagination
        :page-sizes="[10, 20, 50, 100]"
        :current-page="page.page"
        :page-size="page.limit"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>

    <el-dialog title="Sourcing Quote Detail" :visible.sync="detailVisible" width="760px">
      <div v-if="selectedQuote">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="Quote">{{ selectedQuote.quoteNo }}</el-descriptions-item>
          <el-descriptions-item label="Status">{{ selectedQuote.status }}</el-descriptions-item>
          <el-descriptions-item label="Style Code">
            {{ selectedQuote.styleCode }}
          </el-descriptions-item>
          <el-descriptions-item label="Version">
            {{ selectedQuote.productVersion }}
          </el-descriptions-item>
          <el-descriptions-item label="Customer Size">
            {{ selectedQuote.customerSizeSystem }}
            {{ selectedQuote.customerSize }}
          </el-descriptions-item>
          <el-descriptions-item label="Lookup Size">
            {{ selectedQuote.dewuDisplaySize }}
          </el-descriptions-item>
          <el-descriptions-item label="Final PHP Price">
            {{ displayPrice(selectedQuote) }}
          </el-descriptions-item>
          <el-descriptions-item label="Expires">{{ selectedQuote.expiresAt }}</el-descriptions-item>
          <el-descriptions-item label="Review Required">
            {{ selectedQuote.humanReviewRequired }}
          </el-descriptions-item>
          <el-descriptions-item label="Lookup Result">
            {{ selectedQuote.lookupResultId }}
          </el-descriptions-item>
        </el-descriptions>

        <h4>Product Snapshot</h4>
        <pre class="snapshot">{{ pretty(selectedQuote.productSnapshot) }}</pre>

        <h4>Pricing Inputs</h4>
        <pre class="snapshot">{{ pretty(selectedQuote.pricingInputs) }}</pre>

        <h4>Status History</h4>
        <el-table :data="statusLogs" border size="small">
          <el-table-column prop="fromStatus" label="From" width="150" />
          <el-table-column prop="toStatus" label="To" width="180" />
          <el-table-column prop="reason" label="Reason" min-width="220" />
          <el-table-column prop="operatorName" label="Operator" width="130" />
          <el-table-column prop="createTime" label="Created" width="170" />
        </el-table>
      </div>
      <span slot="footer">
        <el-button @click="detailVisible = false">Close</el-button>
        <el-button type="warning" @click="recheckQuote">Recheck Source</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      statuses: [
        "DRAFT",
        "WAITING_FOR_PRODUCT_CONFIRMATION",
        "WAITING_FOR_SIZE",
        "SOURCE_CHECK_PENDING",
        "SOURCE_CHECKING",
        "AVAILABLE_TO_SOURCE",
        "SOURCE_UNAVAILABLE",
        "QUOTED",
        "CUSTOMER_ACCEPTED",
        "QUOTE_EXPIRED",
        "PRICE_CHANGED",
        "CANCELLED",
      ],
      filters: { status: "" },
      quotes: [],
      page: {
        page: 1,
        limit: 10,
        total: 0,
      },
      selectedQuote: null,
      statusLogs: [],
      detailVisible: false,
    };
  },
  created() {
    this.loadQuotes();
  },
  methods: {
    async loadQuotes() {
      const params = {
        page: this.page.page,
        limit: this.page.limit,
        sort: "id,desc",
      };
      if (this.filters.status) params.status = this.filters.status;
      const data = await this.axios.get("mall:/api/admin/sourcing/quotes", { params });
      this.quotes = data.content || data.items || [];
      this.page.limit = data.size || this.page.limit;
      this.page.total = data.totalElements || data.total || 0;
    },
    async handleFilterChange() {
      this.page.page = 1;
      await this.loadQuotes();
    },
    async handlePageChange(page) {
      this.page.page = page;
      await this.loadQuotes();
    },
    async handleSizeChange(size) {
      this.page.limit = size;
      this.page.page = 1;
      await this.loadQuotes();
    },
    async cancelQuote(row) {
      await this.axios.post(`mall:/api/admin/sourcing/quotes/${row.id}/cancel`, {
        reason: "Cancelled from admin",
      });
      await this.loadQuotes();
    },
    async sendQuote(row) {
      if (!this.isSendable(row)) {
        this.$message.error("Quote is not sendable");
        return;
      }
      try {
        await this.axios.post(`mall:/api/admin/sourcing/quotes/${row.id}/send`);
        this.$message.success("Quote send request recorded");
      } catch (e) {
        this.$message.error(this.errorMessage(e));
      }
      await this.loadQuotes();
    },
    async openDetail(row) {
      const data = await this.axios.get(`mall:/api/admin/sourcing/quotes/${row.id}`);
      this.selectedQuote = data.quote || row;
      this.statusLogs = data.statusLogs || [];
      this.detailVisible = true;
    },
    async recheckQuote() {
      await this.axios.post(`mall:/api/admin/sourcing/quotes/${this.selectedQuote.id}/recheck`, {
        reason: "Recheck requested from admin",
      });
      this.$message.success("Recheck task created");
      await this.openDetail(this.selectedQuote);
      await this.loadQuotes();
    },
    pretty(value) {
      if (!value) return "";
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch (e) {
        return value;
      }
    },
    displayPrice(row) {
      if (!row || row.sourceAvailability !== "AVAILABLE_TO_SOURCE" || !row.finalPricePhp) {
        return "";
      }
      return row.finalPricePhp;
    },
    isSendable(row) {
      return (
        row &&
        row.status === "QUOTED" &&
        row.sourceAvailability === "AVAILABLE_TO_SOURCE" &&
        Number(row.finalPricePhp) > 0 &&
        this.isFuture(row.expiresAt)
      );
    },
    isFuture(value) {
      if (!value) return false;
      const time = new Date(value).getTime();
      return !Number.isNaN(time) && time > Date.now();
    },
    errorMessage(error) {
      return (
        (error && error.response && error.response.data && error.response.data.message) ||
        (error && error.message) ||
        "Quote is not sendable"
      );
    },
  },
};
</script>

<style scoped>
.toolbar {
  margin-bottom: 12px;
}
.pagination {
  margin-top: 12px;
  text-align: right;
}
.snapshot {
  max-height: 180px;
  overflow: auto;
  padding: 10px;
  background: #f7f8fa;
  border: 1px solid #ebeef5;
  white-space: pre-wrap;
}
</style>
